import { apiFetch } from "@/lib/apiClient";
import type { CaregiverCategoryValue } from "@/shared/types/family";
import type {
  AccessActivityEvent,
  CaregiverPatientAccessDetail,
  CaregiverPatientSummary,
  IncomingCaregiverInvite,
} from "@/shared/types/caregiverPortal";

interface ApiResult<T> {
  data: T | null;
  error: string | null;
  code: string | null;
}

function toResult<T>(data: T | null, error?: { message: string; code: string } | null): ApiResult<T> {
  if (error) return { data: null, error: error.message, code: error.code };
  return { data, error: null, code: null };
}

type RawCaregiverPatientSummary = Partial<CaregiverPatientSummary> & {
  patientId?: string;
  patient_id?: string;
  patientName?: string;
  patient_name?: string;
  accessStatus?: string;
  access_status?: string;
  categories_shared_count?: number;
  categories_total_count?: number;
};

interface PatientListResponse {
  data: RawCaregiverPatientSummary[];
}

interface InviteListResponse {
  data: IncomingCaregiverInvite[];
}

interface ActivityListResponse {
  data: AccessActivityEvent[];
}

// Everything below reuses the patient portal's real backend (`portal: "patient"`,
// under /api/patient/family/*) — there's no separate caregiver auth/backend yet,
// by design (see CaregiverProtectedLayout's docstring). A caregiver and a patient
// are the same account; CareRelationship is what actually tracks who's viewing
// whose records, regardless of which portal UI they're currently in.

export async function listMyPatientsApi() {
  const { data, error } = await apiFetch<PatientListResponse>("/api/patient/family/my-patients", {
    method: "GET",
    portal: "patient",
  });
  if (error) return toResult<CaregiverPatientSummary[]>(null, error);

  const patients = (data?.data ?? []).map((patient) => {
    const rawStatus = String(patient.status ?? patient.accessStatus ?? patient.access_status ?? "active").toLowerCase();
    const status: CaregiverPatientSummary["status"] =
      rawStatus === "revoked"
        ? "revoked"
        : ["pending", "pending_approval", "request_pending", "requested", "awaiting_approval"].includes(rawStatus)
          ? "pending"
          : "active";

    return {
      id: String(patient.id ?? patient.patientId ?? patient.patient_id ?? ""),
      name: String(patient.name ?? patient.patientName ?? patient.patient_name ?? "Patient"),
      status,
      categoriesSharedCount: Number(patient.categoriesSharedCount ?? patient.categories_shared_count ?? 0),
      categoriesTotalCount: Number(patient.categoriesTotalCount ?? patient.categories_total_count ?? 0),
    };
  }).filter((patient) => patient.id);

  return toResult(patients);
}

export async function listIncomingInvitesApi() {
  const { data, error } = await apiFetch<InviteListResponse>("/api/patient/family/access-requests", {
    method: "GET",
    portal: "patient",
  });
  return toResult(data?.data ?? null, error);
}

export async function acceptInviteApi(inviteId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/access-requests/${inviteId}/accept`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

export async function declineInviteApi(inviteId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/access-requests/${inviteId}/decline`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

/**
 * NOT YET WIRED — no backend endpoint exists for a per-patient detailed
 * access view with real shared-record content (that's the deferred
 * dashboard-data-scoping follow-up). Left pointing at the old placeholder
 * path; callers already degrade gracefully on failure (see
 * CaregiverDashboardView's isDetailLoading/EmptyState handling).
 */
export async function getPatientAccessDetailApi(patientId: string) {
  const { data, error } = await apiFetch<CaregiverPatientAccessDetail>(`/api/caregiver/patients/${patientId}/access`, {
    method: "GET",
    portal: "patient",
  });
  return toResult(data, error);
}

export async function listAccessActivityApi() {
  const { data, error } = await apiFetch<ActivityListResponse>("/api/caregiver/access-activity", {
    method: "GET",
    portal: "patient",
  });
  return toResult(data?.data ?? null, error);
}

/**
 * Direction A — requests access to a patient's records by email. Never
 * reveals whether the email belongs to a real account (no live "verified"
 * pre-check exists on the backend, by design — see
 * FamilyService.request_patient_access) — a non-existent or unverified
 * email gets back the same generic failure as any other rejection.
 */
export async function requestPatientAccessApi(email: string) {
  const { data, error } = await apiFetch<{ message?: string }>("/api/patient/family/patient-access-requests", {
    method: "POST",
    body: JSON.stringify({ email }),
    portal: "patient",
  });
  return toResult(data, error);
}

export async function requestMoreCategoriesApi(patientId: string, categories: CaregiverCategoryValue[]) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/caregiver/patients/${patientId}/request-categories`, {
    method: "POST",
    body: JSON.stringify({ categories }),
    portal: "patient",
  });
  return toResult(data, error);
}
